import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Send, MailCheck, X, UserRound } from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import AssetHoverLabel from '../components/AssetHoverLabel';
import SecurityFilterBar from '../components/SecurityFilterBar';
import TenantLink from '../components/TenantLink';
import ColumnPicker, { ColumnDef, DraggableTh, SortValue, useColumnPrefs, useColumnSort } from '../components/ColumnPicker';
import StatusSelect, { STATUS_LABELS } from '../components/StatusSelect';
import { countryTag } from '../utils/country';
import OutcomeBadge, { EventOutcome } from '../components/OutcomeBadge';
import CustomerIpBadge from '../components/CustomerIpBadge';
import TenantSelect from '../components/TenantSelect';

interface CaseRow {
  id: number;
  outcome: EventOutcome | null;
  tenant_id: number;
  tenant_name?: string;
  tenant_contact_email: string | null;
  title: string;
  status: 'new' | 'investigating' | 'contained' | 'closed';
  severity: 'low' | 'medium' | 'high' | 'critical';
  mitre_tactic: string | null;
  mitre_technique: string | null;
  entity_host: string | null;
  entity_host_label: string | null;
  // Asse identità (2026-09-18): account coinvolto e paesi di origine degli alert.
  entity_user: string | null;
  src_countries: string[] | null;
  // IP sorgente degli alert che sono IP pubblici del cliente, con l'etichetta del blocco.
  customer_ips: { ip: string; label: string | null; siteName: string | null }[];
  asset_id: number | null;
  asset_ip_address: string | null;
  asset_mac_address: string | null;
  asset_business_critical: boolean | null;
  assigned_user_name: string | null;
  assigned_user_id: number | null;
  alert_count: number;
  device_sources: string[] | null;
  device_products: string[] | null;
  description: string | null;
  note_count: number;
  rule_names: string[] | null;
  last_reported_at: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
}

// "Fixing Status" nell'interfaccia richiama il linguaggio comune dei prodotti XDR/SIEM
// (es. Pending/Fixing/Fixed) pur mantenendo i 4 stati interni new/investigating/contained/closed.
const SEVERITY_LABELS: Record<string, string> = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };

const SEVERITY_BADGE: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-700',
  critical: 'bg-red-600 text-white',
};

// Assessment in stile XDR derivato dalla severità interna.
const ASSESSMENT: Record<string, { label: string; badge: string }> = {
  critical: { label: 'Malicious', badge: 'bg-red-100 text-red-700' },
  high: { label: 'Malicious', badge: 'bg-red-100 text-red-700' },
  medium: { label: 'Suspicious', badge: 'bg-amber-100 text-amber-700' },
  low: { label: 'Informational', badge: 'bg-gray-100 text-gray-600' },
};

// Finestra di default per non caricare l'intera storia: un tenant con integrazioni attive può
// avere centinaia di migliaia di righe nel tempo, e senza un filtro iniziale la pagina prova a
// ordinarle/aggregarle tutte prima di mostrare qualcosa. L'analista può comunque allargarla o
// azzerarla dai campi data.
function defaultSince(hours: number): string {
  const d = new Date(Date.now() - hours * 3_600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Device Source: prodotto/sensore che ha generato la detection (vedi routes/cases.ts). I codici
// delle integrazioni diventano nomi leggibili; vendor/prodotto dei Log Source restano come sono,
// gli altri codici minuscoli (vendor EDR: sentinelone, acronis…) prendono l'iniziale maiuscola.
const DEVICE_SOURCE_LABELS: Record<string, string> = {
  syslog: 'Syslog', cef: 'CEF', firewall: 'Firewall', windows: 'Windows', dhcp: 'DHCP', edr: 'EDR',
  entra_id: 'Microsoft Entra ID', asset_monitor: 'Asset Monitor', ndr: 'NDR', vmware: 'VMware vCenter',
  sentinelone: 'SentinelOne',
};
function deviceSources(c: CaseRow): string | undefined {
  return c.device_products?.map(p => DEVICE_SOURCE_LABELS[p] ?? (/^[a-z_]+$/.test(p) ? p.charAt(0).toUpperCase() + p.slice(1) : p)).join(', ');
}

function fmtDuration(fromIso: string, toIso: string): string {
  const m = Math.floor((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60000);
  if (m < 0) return '—';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

// Colonne disponibili (ordine di visualizzazione); i campi senza dato in piattaforma mostrano "—".
const CASE_COLUMNS: ColumnDef[] = [
  { key: 'severity', label: 'Incident Severity', defaultVisible: true },
  { key: 'outcome', label: 'Esito', defaultVisible: true },
  { key: 'tenant', label: 'Tenant', defaultVisible: true },
  { key: 'name', label: 'Incident Name', defaultVisible: true },
  { key: 'assessment', label: 'Incident Assessment', defaultVisible: true },
  { key: 'asset_group', label: 'Asset Group', defaultVisible: false },
  { key: 'device_source', label: 'Device Source', defaultVisible: true },
  { key: 'affected_asset', label: 'Affected Asset', defaultVisible: true },
  { key: 'affected_user', label: 'Affected User', defaultVisible: true },
  { key: 'origin', label: 'Origin', defaultVisible: false },
  { key: 'period', label: 'Period', defaultVisible: true },
  { key: 'fixing_status', label: 'Fixing Status', defaultVisible: true },
  { key: 'incident_engine', label: 'Incident Engine', defaultVisible: false },
  { key: 'description', label: 'Description', defaultVisible: true },
  { key: 'aggregated_alerts', label: 'Aggregated Alerts', defaultVisible: true },
  { key: 'data_source', label: 'Data Source', defaultVisible: false },
  { key: 'incident_id', label: 'Incident ID', defaultVisible: false },
  { key: 'l1_category', label: 'L1 Incident Category', defaultVisible: false },
  { key: 'l2_category', label: 'L2 Incident Category', defaultVisible: false },
  { key: 'agent_status', label: 'Agent Status', defaultVisible: false },
  { key: 'tag', label: 'Tag', defaultVisible: false },
  { key: 'first_detected', label: 'First Detected', defaultVisible: false },
  { key: 'last_detected', label: 'Last Detected', defaultVisible: false },
  { key: 'remarks', label: 'Remarks', defaultVisible: false },
  { key: 'latency', label: 'Latency', defaultVisible: false },
  { key: 'source', label: 'Source', defaultVisible: false },
  { key: 'whitelisted', label: 'Whitelisted Status', defaultVisible: false },
  { key: 'rule_id', label: 'Incident Rule ID', defaultVisible: false },
  { key: 'source_platform', label: 'Source Platform', defaultVisible: false },
  // Assegnatario (2026-10-20): menu per riga, assegnare = presa in carico.
  { key: 'owner', label: 'Assigned to', defaultVisible: true },
  { key: 'mdr_id', label: 'MDR Incident ID', defaultVisible: false },
  { key: 'fixing_duration', label: 'Fixing Duration', defaultVisible: false },
  { key: 'block_duration', label: 'Block Duration', defaultVisible: false },
  { key: 'host_ip', label: 'Host IP Services', defaultVisible: false },
  { key: 'asset_type', label: 'Asset Type', defaultVisible: false },
  { key: 'last_auth_user', label: 'Last Auth User', defaultVisible: false },
  { key: 'auth_user', label: 'Auth User', defaultVisible: false },
  { key: 'target_app', label: 'Target Business Application', defaultVisible: false },
];

const SEVERITY_RANK: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 4 };
const STATUS_RANK: Record<string, number> = { new: 1, investigating: 2, contained: 3, closed: 4 };

// Valore con cui ordinare una colonna (vedi useColumnSort in ColumnPicker.tsx): le colonne che
// mostrano sempre "—" non ordinano nulla.
function sortValue(c: CaseRow, key: string): SortValue {
  switch (key) {
    case 'severity': case 'assessment': return SEVERITY_RANK[c.severity];
    case 'outcome': return c.outcome;
    case 'tenant': return c.tenant_name;
    case 'name': return c.title;
    case 'device_source': return deviceSources(c);
    case 'affected_asset': return c.entity_host_label ? `${c.entity_host_label} · ${c.entity_host}` : c.entity_host;
    case 'affected_user': return c.entity_user;
    case 'origin': return c.customer_ips?.length ? 'Customer' : c.src_countries?.join(', ');
    case 'period': case 'first_detected': return c.created_at;
    case 'last_detected': return c.updated_at;
    case 'fixing_status': return STATUS_RANK[c.status];
    case 'description': return c.description;
    case 'aggregated_alerts': return c.alert_count;
    case 'data_source': case 'source': return c.device_sources?.join(', ');
    case 'incident_id': case 'mdr_id': return c.id;
    case 'l1_category': return c.mitre_tactic;
    case 'l2_category': return c.mitre_technique;
    case 'remarks': return c.note_count;
    case 'latency': return c.last_reported_at ? new Date(c.last_reported_at).getTime() - new Date(c.created_at).getTime() : null;
    case 'rule_id': return c.rule_names?.join(', ');
    case 'owner': return c.assigned_user_name;
    case 'fixing_duration': return c.closed_at ? new Date(c.closed_at).getTime() - new Date(c.created_at).getTime() : null;
    case 'host_ip': return c.asset_ip_address;
    default: return null;
  }
}

// Bozza precompilata della segnalazione al cliente: l'analista può modificarla prima dell'invio.
function buildDefaultReport(c: CaseRow): string {
  const asset = [c.entity_host, c.asset_ip_address].filter(Boolean).join(' / ') || 'not identified';
  return [
    `Dear Customer,`,
    ``,
    `our SOC team detected a security incident on your infrastructure.`,
    ``,
    `Incident: ${c.title}`,
    `Severity: ${SEVERITY_LABELS[c.severity]}`,
    `Affected asset: ${asset}`,
    `MITRE tactic: ${c.mitre_tactic || 'n/a'}`,
    `Detected at: ${new Date(c.created_at).toLocaleString('en-GB')}`,
    `Related alerts: ${c.alert_count}`,
    `Current status: ${STATUS_LABELS[c.status]}`,
    ``,
    `Our analysts are handling the incident and will keep you updated on the remediation activities.`,
    ``,
    `Best regards,`,
    `SOC Team`,
  ].join('\n');
}

// Elenca i Security Incidents per il tenant selezionato, oppure di tutti i tenant accessibili
// quando lo scope corrente è "All tenants" (vedi selettore in sidebar).
export default function CasesPage() {
  const { selectedTenantId } = useTenantScope();
  const [cases, setCases] = useState<CaseRow[]>([]);
  // Utenti assegnabili: attivi, admin o analyst, nello scope di chi guarda (GET /api/users).
  const [assignees, setAssignees] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    axios.get('/api/users').then(res => {
      setAssignees((res.data.data as any[]).filter(u => u.active && (u.role === 'admin' || u.role === 'analyst')).map(u => ({ id: u.id, name: u.name })));
    }).catch(() => setAssignees([]));
  }, []);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Default High/Critical (26/10/2026, richiesta dell'utente): si apre su ciò che va guardato
  // subito; le altre severità si aggiungono da Filters.
  const [severities, setSeverities] = useState<Set<string>>(() => new Set(['high', 'critical']));
  const [statuses, setStatuses] = useState<Set<string>>(new Set());
  // Default (26/10/2026): ultime 24 ore e refresh automatico ogni 5 minuti.
  const [fromDate, setFromDate] = useState<string>(() => defaultSince(24));
  const [toDate, setToDate] = useState<string>('');
  const [hideNewAssets, setHideNewAssets] = useState(true);
  const [hideEdrGap, setHideEdrGap] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(300);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [reportCase, setReportCase] = useState<CaseRow | null>(null);
  const [reportSubject, setReportSubject] = useState('');
  const [reportBody, setReportBody] = useState('');
  const [reportError, setReportError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const { orderedDefs, visible, setVisible: setVisibleColumns, moveColumn, reset: resetColumns } = useColumnPrefs('soc.columns.incidents', CASE_COLUMNS);
  const getSortValue = useCallback(sortValue, []);
  const { sort, toggleSort, sorted: sortedCases } = useColumnSort(cases, getSortValue);

  // Colonne effettivamente mostrate (nell'ordine scelto): Tenant solo nella vista cross-tenant.
  const shownColumns = orderedDefs.filter(d => visible.has(d.key) && (d.key !== 'tenant' || !selectedTenantId));
  const colCount = shownColumns.length + 3;

  async function load() {
    setLoading(true);
    const res = await axios.get('/api/cases', {
      params: {
        tenantId: selectedTenantId ?? undefined,
        search: search || undefined,
        status: statuses.size > 0 ? [...statuses].join(',') : undefined,
        severity: severities.size > 0 ? [...severities].join(',') : undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        hideNewAssets: hideNewAssets || undefined,
        hideEdrGap: hideEdrGap || undefined,
      },
    });
    setCases(res.data.data);
    setSelected(new Set());
    setLoading(false);
  }

  useEffect(() => { load(); }, [selectedTenantId, statuses, severities, fromDate, toDate, hideNewAssets, hideEdrGap]);

  // Ricerca testuale con debounce leggero per non interrogare ad ogni battitura.
  useEffect(() => {
    const timeout = setTimeout(load, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(load, autoRefresh * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedTenantId, statuses, severities, fromDate, toDate, hideNewAssets, hideEdrGap]);

  function toggleSelected(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected(prev => (prev.size === cases.length ? new Set() : new Set(cases.map(c => c.id))));
  }

  async function bulkSetStatus(status: string) {
    if (selected.size === 0) return;
    await axios.put('/api/cases/bulk/status', { caseIds: [...selected], status });
    load();
  }

  // Assegnazione (riga singola o selezione): il backend la tratta come presa in carico
  // (stato new → investigating, acknowledged_at). Vuoto = togli l'assegnazione.
  async function assignCases(caseIds: number[], assignedUserId: number | null) {
    if (caseIds.length === 0) return;
    try {
      await axios.put('/api/cases/bulk/assign', { caseIds, assignedUserId });
      load();
    } catch (err: any) {
      window.alert(err.response?.data?.error || 'Could not assign the selected incidents');
    }
  }

  async function setCaseStatus(caseId: number, status: string) {
    setCases(prev => prev.map(c => (c.id === caseId ? { ...c, status: status as CaseRow['status'] } : c)));
    await axios.put(`/api/cases/${caseId}`, { status });
    load();
  }

  function openReport(c: CaseRow) {
    setReportCase(c);
    setReportSubject(`[SOC] ${SEVERITY_LABELS[c.severity]} incident: ${c.title}`);
    setReportBody(buildDefaultReport(c));
    setReportError(null);
  }

  async function sendReport() {
    if (!reportCase) return;
    setSending(true);
    setReportError(null);
    try {
      await axios.post(`/api/cases/${reportCase.id}/report`, { subject: reportSubject, message: reportBody });
      setReportCase(null);
      load();
    } catch (err: any) {
      setReportError(err.response?.data?.error || 'Error while sending the report');
    } finally {
      setSending(false);
    }
  }

  function renderCell(c: CaseRow, key: string) {
    switch (key) {
      case 'severity':
        return <span className={`badge ${SEVERITY_BADGE[c.severity]}`}>{SEVERITY_LABELS[c.severity]}</span>;
      case 'outcome':
        return <OutcomeBadge outcome={c.outcome} />;
      case 'tenant':
        return <TenantLink tenantId={c.tenant_id} name={c.tenant_name} />;
      case 'name':
        return (
          <span className="font-medium text-primary-700">
            <Link to={`/incidents/${c.id}`}>{c.title}</Link>
            {/* Accanto al nome, non solo nella colonna Origin (nascosta di default): l'evidenza
                "è il cliente stesso" deve vedersi senza configurare le colonne. */}
            {c.customer_ips?.length > 0 && (
              <span className="ml-2"><CustomerIpBadge label={c.customer_ips[0].label} siteName={c.customer_ips[0].siteName} compact={c.customer_ips.length > 1} /></span>
            )}
            {c.last_reported_at && (
              <span className="ml-2 inline-flex items-center gap-1 badge bg-emerald-100 text-emerald-700"
                title={`Reported on ${new Date(c.last_reported_at).toLocaleString('en-GB')}`}>
                <MailCheck size={11} /> Reported
              </span>
            )}
          </span>
        );
      case 'assessment': {
        const a = ASSESSMENT[c.severity];
        return <span className={`badge ${a.badge}`}>{a.label}</span>;
      }
      case 'asset_group': return <span className="text-gray-400">—</span>;
      case 'device_source': return <span className="text-gray-500">{deviceSources(c) || '—'}</span>;
      case 'affected_asset':
        return c.entity_host ? (
          <span className="inline-flex items-center gap-1.5">
            <AssetHoverLabel hostname={c.entity_host} label={c.entity_host_label} ipAddress={c.asset_ip_address} macAddress={c.asset_mac_address} assetId={c.asset_id} />
            {c.asset_business_critical && (
              <span className="badge bg-red-100 text-red-700" title="Business Critical Asset">BCA</span>
            )}
          </span>
        ) : <span className="text-gray-400">—</span>;
      case 'affected_user':
        return c.entity_user
          ? <span className="inline-flex items-center gap-1 text-gray-800"><UserRound size={13} className="text-violet-500" />{c.entity_user}</span>
          : <span className="text-gray-400">—</span>;
      case 'origin':
        return c.src_countries?.length || c.customer_ips?.length
          ? <span className="inline-flex flex-wrap gap-1">
              {c.customer_ips?.map(x => <CustomerIpBadge key={x.ip} label={x.label} siteName={x.siteName} />)}
              {c.src_countries?.map(cc => { const t = countryTag(cc); return t ? <span key={cc} className="badge bg-gray-100 text-gray-700" title={t.title}>{t.text}</span> : null; })}
            </span>
          : <span className="text-gray-400">—</span>;
      case 'period':
        return (
          <span className="text-gray-500 whitespace-nowrap">
            {new Date(c.created_at).toLocaleString('en-GB')} → {new Date(c.updated_at).toLocaleString('en-GB')}
          </span>
        );
      case 'fixing_status':
        return <StatusSelect value={c.status} onChange={s => setCaseStatus(c.id, s)} />;
      case 'incident_engine': return <span className="badge bg-primary-50 text-primary-700">Correlation Engine</span>;
      case 'description':
        return <span className="text-gray-600 text-xs" title={c.description || ''}>
          {c.description ? (c.description.length > 100 ? c.description.slice(0, 100) + '…' : c.description) : '—'}
        </span>;
      case 'aggregated_alerts': return <span className="badge bg-gray-100 text-gray-600">{c.alert_count}</span>;
      case 'data_source': return <span className="text-gray-500 uppercase">{c.device_sources?.join(', ') || '—'}</span>;
      case 'incident_id': return <span className="font-mono text-xs text-gray-500">INC-{c.id}</span>;
      case 'l1_category': return <span className="text-gray-500">{c.mitre_tactic || '—'}</span>;
      case 'l2_category': return <span className="text-gray-500">{c.mitre_technique || '—'}</span>;
      case 'agent_status': return <span className="text-gray-400">—</span>;
      case 'tag': return <span className="text-gray-400">—</span>;
      case 'first_detected': return <span className="text-gray-500 whitespace-nowrap">{new Date(c.created_at).toLocaleString('en-GB')}</span>;
      case 'last_detected': return <span className="text-gray-500 whitespace-nowrap">{new Date(c.updated_at).toLocaleString('en-GB')}</span>;
      case 'remarks': return <span className="text-gray-500">{c.note_count > 0 ? `${c.note_count} note${c.note_count > 1 ? 's' : ''}` : '—'}</span>;
      case 'latency': return <span className="text-gray-500">{c.last_reported_at ? fmtDuration(c.created_at, c.last_reported_at) : '—'}</span>;
      case 'source': return <span className="text-gray-500 uppercase">{c.device_sources?.join(', ') || '—'}</span>;
      case 'whitelisted': return <span className="text-gray-400">—</span>;
      case 'rule_id': return <span className="text-gray-500">{c.rule_names?.join(', ') || '—'}</span>;
      case 'source_platform': return <span className="text-gray-500">SOC Platform</span>;
      case 'owner': return (
        <select value={c.assigned_user_id ?? ''} onChange={e => assignCases([c.id], e.target.value ? Number(e.target.value) : null)}
          onClick={e => e.stopPropagation()} className="input-field py-0.5 px-1.5 text-xs max-w-[160px]" title="Assigning takes the incident in charge">
          <option value="">— unassigned —</option>
          {assignees.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      );
      case 'mdr_id': return <span className="font-mono text-xs text-gray-500">MDR-{c.id}</span>;
      case 'fixing_duration': return <span className="text-gray-500">{c.closed_at ? fmtDuration(c.created_at, c.closed_at) : '—'}</span>;
      case 'block_duration': return <span className="text-gray-400">—</span>;
      case 'host_ip': return <span className="text-gray-500 font-mono text-xs">{c.asset_ip_address || '—'}</span>;
      case 'asset_type': return <span className="text-gray-400">—</span>;
      case 'last_auth_user': return <span className="text-gray-400">—</span>;
      case 'auth_user': return <span className="text-gray-400">—</span>;
      case 'target_app': return <span className="text-gray-400">—</span>;
      default: return null;
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Security Incidents</h1>
        <TenantSelect />
      </div>

      <SecurityFilterBar
        search={search}
        onSearchChange={setSearch}
        severities={severities}
        onSeveritiesChange={setSeverities}
        statuses={statuses}
        onStatusesChange={setStatuses}
        fromDate={fromDate}
        onFromDateChange={setFromDate}
        toDate={toDate}
        onToDateChange={setToDate}
        hideNewAssets={hideNewAssets}
        onHideNewAssetsChange={setHideNewAssets}
        hideEdrGap={hideEdrGap}
        onHideEdrGapChange={setHideEdrGap}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
        onRefresh={load}
        loading={loading}
        items={cases.map(c => ({ severity: c.severity, mitre_tactic: c.mitre_tactic }))}
      />

      {selected.size > 0 && (
        <div className="card p-3 mb-3 flex items-center gap-2 bg-primary-50 border-primary-200">
          <span className="text-sm text-primary-800 font-medium">{selected.size} selected</span>
          <button onClick={() => bulkSetStatus('investigating')} className="btn-secondary">Mark as Fixing</button>
          <button onClick={() => bulkSetStatus('closed')} className="btn-secondary">Mark as Fixed</button>
          <select value="" onChange={e => { const v = e.target.value; if (v === '') return; assignCases([...selected], v === 'none' ? null : Number(v)); }}
            className="input-field py-1 text-sm" title="Assigning takes the incidents in charge (status Fixing)">
            <option value="">Assign to…</option>
            {assignees.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            <option value="none">Unassign</option>
          </select>
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="table-header-sm w-8">
                <input type="checkbox" checked={selected.size > 0 && selected.size === cases.length} onChange={toggleSelectAll} />
              </th>
              {shownColumns.map(d => (
                <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} sort={sort} onSort={toggleSort} className="table-header-sm whitespace-nowrap">
                  {d.label}
                </DraggableTh>
              ))}
              <th className="table-header-sm text-right">Report</th>
              <th className="table-header-sm w-8 text-right">
                <ColumnPicker defs={orderedDefs} visible={visible} onChange={setVisibleColumns} onReset={resetColumns} onMove={moveColumn} />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="table-cell-sm text-gray-400" colSpan={colCount}>Loading…</td></tr>}
            {!loading && cases.length === 0 && (
              <tr><td className="table-cell-sm text-gray-400" colSpan={colCount}>No incident found</td></tr>
            )}
            {sortedCases.map(c => (
              <tr key={c.id}
                className={`border-t border-gray-100 hover:bg-gray-50 ${
                  c.asset_business_critical ? 'bg-red-50/70 border-l-4 border-l-red-400'
                  : c.last_reported_at ? 'bg-emerald-50/70 border-l-4 border-l-emerald-400' : ''}`}>
                <td className="table-cell-sm">
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelected(c.id)} />
                </td>
                {shownColumns.map(d => <td key={d.key} className="table-cell-sm">{renderCell(c, d.key)}</td>)}
                <td className="table-cell-sm text-right" colSpan={2}>
                  <button onClick={() => openReport(c)} disabled={!c.tenant_contact_email}
                    title={c.tenant_contact_email
                      ? `Send a report to ${c.tenant_contact_email}`
                      : 'No contact email configured for this tenant'}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                      c.tenant_contact_email
                        ? 'text-primary-700 bg-primary-50 hover:bg-primary-100'
                        : 'text-gray-300 bg-gray-50 cursor-not-allowed'}`}>
                    <Send size={13} /> Report
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reportCase && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Send size={16} className="text-primary-600" /> Send report to customer
              </h2>
              <button onClick={() => setReportCase(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              {reportError && <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{reportError}</div>}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Recipient</label>
                <div className="text-sm text-gray-800 bg-gray-50 rounded-md px-3 py-2">{reportCase.tenant_contact_email}</div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Subject</label>
                <input value={reportSubject} onChange={e => setReportSubject(e.target.value)} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Message</label>
                <textarea value={reportBody} onChange={e => setReportBody(e.target.value)} rows={14}
                  className="input-field w-full font-mono text-xs" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={() => setReportCase(null)} className="btn-secondary">Cancel</button>
              <button onClick={sendReport} disabled={sending || !reportBody.trim()} className="btn-primary flex items-center gap-1">
                <Send size={14} /> {sending ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
